import { endOfDay, format, startOfDay } from 'date-fns';

// ── Preset types ────────────────────────────────────────────────────────────

export const LAST_UPDATED_PRESET_VALUES = [
  'none',
  'today',
  'last7',
  'last30',
  'thisYear',
  'lastYear',
  'custom',
] as const;

export type LastUpdatedPreset = (typeof LAST_UPDATED_PRESET_VALUES)[number];

export type PresetRowId = Exclude<LastUpdatedPreset, 'none' | 'custom'>;

export type PresetDef = {
  id: PresetRowId;
  label: string;
};

// ── Shared UI helpers (used by both date filter variants) ───────────────────

export function boundsToIso(
  from: Date,
  to: Date
): { from: string; to: string } {
  return {
    from: startOfDay(from).toISOString(),
    to: endOfDay(to).toISOString(),
  };
}

export function getPresetRows(now: Date): PresetDef[] {
  const y = now.getFullYear();
  const prevY = y - 1;
  return [
    { id: 'today', label: 'Today' },
    { id: 'last7', label: 'Last 7 days' },
    { id: 'last30', label: 'Last 30 days' },
    { id: 'thisYear', label: `This year (${y})` },
    { id: 'lastYear', label: `Last year (${prevY})` },
  ];
}

export function presetSummary(id: PresetRowId, now: Date): string {
  return getPresetRows(now).find(r => r.id === id)?.label ?? id;
}

export function formatCustomSummary(fromIso: string, toIso: string): string {
  const fromD = new Date(fromIso);
  const toD = new Date(toIso);
  if (Number.isNaN(fromD.getTime()) || Number.isNaN(toD.getTime())) {
    return 'Custom range';
  }
  const sameYear = fromD.getFullYear() === toD.getFullYear();
  if (sameYear) {
    return `${format(fromD, 'MMM d')} – ${format(toD, 'MMM d, yyyy')}`;
  }
  return `${format(fromD, 'MMM d, yyyy')} – ${format(toD, 'MMM d, yyyy')}`;
}

// ── Backend-mirroring bounds logic ──────────────────────────────────────────

function parseQueryDate(value: string | undefined): Date | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms);
}

function subMs(d: Date, ms: number): Date {
  return new Date(d.getTime() - ms);
}

function startOfUtcYear(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
}

function endOfUtcYear(d: Date): Date {
  const y = d.getUTCFullYear();
  return new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999));
}

function startOfUtcYearForYear(year: number): Date {
  return new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
}

function endOfUtcYearForYear(year: number): Date {
  return new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
}

/**
 * Mirrors `getLastUpdatedBounds` in list-all-documents.usecase.ts so rolling
 * presets stay accurate without backend preset fields on ListUpdatesQuery.
 */
export function boundsForDatePreset(
  preset: Exclude<LastUpdatedPreset, 'none' | 'custom'>,
  now: Date
): { from: Date; to: Date } {
  if (preset === 'today') {
    return { from: subMs(now, 24 * 60 * 60 * 1000), to: now };
  }
  if (preset === 'last7') {
    return { from: subMs(now, 7 * 24 * 60 * 60 * 1000), to: now };
  }
  if (preset === 'last30') {
    return { from: subMs(now, 30 * 24 * 60 * 60 * 1000), to: now };
  }
  if (preset === 'thisYear') {
    return { from: startOfUtcYear(now), to: endOfUtcYear(now) };
  }
  if (preset === 'lastYear') {
    const y = now.getUTCFullYear() - 1;
    return {
      from: startOfUtcYearForYear(y),
      to: endOfUtcYearForYear(y),
    };
  }
  return { from: now, to: now };
}

export function listUpdatesDateRangeFromPresetParams(args: {
  preset: LastUpdatedPreset | null;
  from?: string | null;
  to?: string | null;
  now?: Date;
}): { from?: string; to?: string } {
  const now = args.now ?? new Date();
  const preset = args.preset ?? 'none';

  if (preset === 'none') {
    return {};
  }

  if (preset === 'custom') {
    const fromParam = parseQueryDate(args.from ?? undefined);
    const toParam = parseQueryDate(args.to ?? undefined);
    if (!fromParam || !toParam) return {};
    return { from: fromParam.toISOString(), to: toParam.toISOString() };
  }

  const { from, to } = boundsForDatePreset(preset, now);
  return { from: from.toISOString(), to: to.toISOString() };
}
